<template>
  <div class="form-builder-view">
    <LoadingSpinner class="preview-loader" v-if="isLoading" />
    <iframe
      v-if="isHTMLResponse"
      :srcdoc="workflowResponse"
      style="width: 100%; height: 100%; border: none"
    ></iframe>
    <JsonViewer v-if="isJSONResponse" :value="workflowResponse"> </JsonViewer>
    <FormBuilder
      v-if="isFormResponse"
      :key="formRenderKey"
      :elements="task.props?.elements"
      :settings="settings"
      :brandedStyle="formStyle"
      :responseData="responseData"
      :processID="processID"
      :version="version"
      :isPreview="true"
      :currentScreen="currentScreen"
      :tabStructure="task?.props?.structure"
      :flowSettings="flowSettings"
      @handleSubmit="handleSubmit"
    />
    <PageNotFound
      v-if="flowNotFound && !isLoading"
      :isBackButtonVisible="false"
    />
  </div>
</template>
<script>
import ElementRenderer from '@/components/widgets/FormBuilderElementComponent.vue';
import PageNotFound from '@/components/PageNotFound.vue';
import { axiosGet } from '@/plugins/axios.plugins';
import FormBuilder from '@/components/FormBuilder/FormBuilderV3.vue';
import LoadingSpinner from '@/components/widgets/LoadingSpinner.vue';
import { runFlowByID } from '@/services/axios/flow-runner.api.js';
import JsonViewer from 'vue-json-viewer';
import { vueBasedDefaultProcessRunnerVersion } from '@/utils/constants/WorkFlowConstants.js';
import { showToaster } from '@/utils/helpers';
import {
  resolveResponseType,
  isHtmlResponseType,
  isJsonResponseType,
  isFormResponseType,
} from '@/utils/helpers/FormBuilderHelper';
import { checkUrlPermission } from '../../utils/helpers/WorkFlowHelpers';

export default {
  name: 'ProcessPreview',
  data() {
    return {
      task: {},
      flowNotFound: false,
      responseType: null,
      workflowResponse: null,
      isLoading: true,
      responseData: {
        responsePayload: null,
        responseType: null,
        isLoading: false,
      },
      formRenderKey: 0,
      currentScreen: 'desktop',
      settings: {
        btnCaption: 'Starten',
        layout: {
          spacing: 'standard',
        },
      },
      flowExecutionURL: null,
      formStyle: null,
      flowSettings: {},
    };
  },
  props: {
    processID: {
      type: String | Number,
      default: () => '',
      required: false,
    },
    version: {
      type: String,
      default: () => vueBasedDefaultProcessRunnerVersion,
    },
    isActionFlow: {
      type: Boolean,
      default: () => false,
    },
    params: {
      type: String,
      required: false,
      default: '',
    },
    executionUrl: {
      type: String,
      default: null,
    },
    useContainerWidthForResponsive: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    getFlowRunParams() {
      const rawParams = String(this.params || '').trim();
      const normalizedParams = rawParams ? rawParams.replace(/^\?+/, '') : '';
      const hasS3Key = /(^|&)s3_key=/.test(normalizedParams);

      if (hasS3Key) {
        return normalizedParams ? `?${normalizedParams}` : '';
      }

      if (this.processID) {
        return normalizedParams
          ? `?id=${this.processID}&${normalizedParams}`
          : `?id=${this.processID}`;
      }

      return normalizedParams ? `?${normalizedParams}` : '';
    },
    async handleSubmit(formData) {
      // TODO handle submit
      if (!formData) return;
      const useFrame = this.hasResultFrame;
      const flowParams = this.getFlowRunParams();
      try {
        if (useFrame) {
          this.responseData.isLoading = true;
          this.isLoading = false;
        } else {
          this.isLoading = true;
          this.responseData.isLoading = false;
        }
        let response;
        const fd = new FormData();
        const params = new URLSearchParams(
          String(flowParams || '').replace(/^\?/, '')
        );
        const s3KeyFromParams = params.get('s3_key');
        const hasS3Key = Boolean(s3KeyFromParams);
        for (const key in formData) {
          if (key === 'id') {
            if (hasS3Key) {
              fd.append('s3_key', s3KeyFromParams);
            } else {
              fd.append('id', formData[key]);
            }
            continue;
          }
          if (key === 's3_key') {
            continue;
          }
          if (formData[key] instanceof FileList) {
            fd.append(key, formData[key][0]); // take the first file
          } else {
            fd.append(key, formData[key]);
          }
        }

        response = await runFlowByID(this.getFlowExecutionURL, [], fd);

        this.responseData.responsePayload = null;
        this.responseData.responseType = null;
        const isFormResponse = this.isFormResponsePayload(response?.data);
        if (useFrame && !isFormResponse) {
          const resolvedType = this.resolveNonFormResponseType(response);
          this.responseType = 'form';
          this.workflowResponse = null;
          this.responseData.responseType = resolvedType;
          this.responseData.responsePayload = response.data;
        } else {
          this.responseData.responseType = null;
          this.parseResponse(response);
        }
      } catch (error) {
        showToaster(
          this,
          error?.data?.message || error?.message || error,
          'Fehlerhaft !',
          'danger'
        );
        // Handle error case
      } finally {
        this.responseData.isLoading = false;
        this.isLoading = false;
      }
    },
    async fetchFormJsonData() {
      try {
        const flowParams = this.getFlowRunParams();
        let response;
        if (!this.isActionFlow) {
          const apiEndpoint = process.env.VUE_APP_FLOW_RUNNER_BASE_URL.includes(
            '127.0.0.1'
          )
            ? process.env.VUE_APP_FLOW_RUNNER_BASE_URL
            : `${process.env.VUE_APP_FLOW_RUNNER_BASE_URL}/${this.version}`;
          const flowURL = `${apiEndpoint}/run_flow_by_id${flowParams}`;
          response = await axiosGet(flowURL);
        } else {
          response = await runFlowByID(`${this.executionUrl}`, flowParams);
        }

        checkUrlPermission(response?.data?.settings?.domains || []);

        this.parseResponse(response);
      } catch (error) {
        showToaster(
          this,
          error?.data?.message || error?.message || error,
          'Fehlerhaft !',
          'danger'
        );
        this.flowNotFound = true;
      } finally {
        this.isLoading = false;
      }
    },

    onScreenResize() {
      const width = this.useContainerWidthForResponsive
        ? this.$el?.getBoundingClientRect?.().width || window.innerWidth || 0
        : window.innerWidth || 0;
      if (width < 768) this.currentScreen = 'mobile';
      else if (width < 1024) this.currentScreen = 'tablet';
      else if (width < 1440) this.currentScreen = 'laptop';
      else this.currentScreen = 'desktop';
    },
    getResponseContentType(flowResponse) {
      const headers = flowResponse?.headers;
      if (!headers) return '';
      if (typeof headers.get === 'function') {
        return headers.get('content-type') || '';
      }
      return headers['content-type'] || headers['Content-Type'] || '';
    },
    isFormResponsePayload(payload) {
      return payload?.show === 'form' || payload?.name === 'Form';
    },
    resolveNonFormResponseType(flowResponse) {
      const contentType = String(
        this.getResponseContentType(flowResponse)
      ).toLowerCase();
      if (contentType.includes('application/json')) {
        return 'json';
      }
      return typeof flowResponse?.data === 'string' ? 'html' : 'json';
    },

    parseResponse(flowResponse) {
      const isFormResponse = this.isFormResponsePayload(flowResponse?.data);

      if (isFormResponse) {
        this.responseType = 'form';
        this.workflowResponse = null;
        this.responseData.responsePayload = null;
        this.responseData.responseType = null;
        this.task = flowResponse?.data?.task;
        this.settings = this.task?.props?.settings;
        this.formStyle = flowResponse?.data?.style;
        this.flowExecutionURL = flowResponse?.data?.settings?.executionEndPoint;
        this.flowSettings = flowResponse?.data?.settings || {};
        this.formRenderKey = Date.now();
        this.removeWindowResizeEvent();
        this.addWindowResizeEvent();
      } else {
        this.responseType = this.resolveNonFormResponseType(flowResponse);
        this.workflowResponse = flowResponse?.data;
        this.responseData.responsePayload = null;
        this.responseData.responseType = null;
      }
    },
    addWindowResizeEvent() {
      if (
        this.task?.props?.settings?.layout?.spacing == 'custom' &&
        !this.isEditable
      ) {
        window.addEventListener('resize', this.onScreenResize);
      }
    },
    removeWindowResizeEvent() {
      if (this.settings?.layout?.spacing == 'custom' && !this.isEditable) {
        window.removeEventListener('resize', this.onScreenResize);
      }
    },
  },

  computed: {
    isHTMLResponse() {
      const resolved = resolveResponseType(
        this.responseType,
        this.workflowResponse
      );
      return isHtmlResponseType(resolved) && !this.isLoading;
    },
    isJSONResponse() {
      const resolved = resolveResponseType(
        this.responseType,
        this.workflowResponse
      );
      return isJsonResponseType(resolved) && !this.isLoading;
    },
    isFormResponse() {
      const resolved = resolveResponseType(
        this.responseType,
        this.workflowResponse
      );
      return (
        isFormResponseType(resolved) && !!this.task?.props?.elements?.length
      );
    },
    hasResultFrame() {
      return (this.task?.props?.elements || []).some(
        (element) => element.type === 'resultframe'
      );
    },
    getFlowExecutionURL() {
      return this.flowExecutionURL || this.executionUrl;
    },
  },

  async created() {
    this.workflowResponse = null;
    if (!this.processID && !this.params) {
      // TODO show propar error page
      this.flowNotFound = true;
    } else {
      this.fetchFormJsonData();
    }
  },
  mounted() {
    this.onScreenResize();
  },
  beforeDestroy() {
    this.removeWindowResizeEvent();
  },

  components: {
    FormBuilder,
    ElementRenderer,
    PageNotFound,
    LoadingSpinner,
    JsonViewer,
  },
};
</script>
<style scoped>
*,
::before,
::after {
  box-sizing: border-box !important;
}

.form-builder-view {
  height: 100%;
  overflow: hidden;
}
::v-deep .modal .modal-close {
  position: absolute;
  top: 32px;
  right: 36px;
  background-image: url(../../assets/img/work-flow-icons/close.svg);
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  width: 14px;
  height: 14px;
  cursor: pointer;
}
.form-builder-view ::v-deep .preview-loader.loading .progress-icon-wrapper {
  transform: translate(-50%, -50%);
  top: auto;
  right: auto;
  bottom: 50%;
  left: 50%;
}
.form-builder-view
  .preview-loader.loading
  ::v-deep
  .progress-icon-wrapper
  .progress-icon {
  position: unset !important;
}
</style>
